// The in-game ☸ OPTIONS menu (ui-map-interface.md §13, R1731; card 20 of
// mockup round 081), as pure markup.
//
// `ui/overlay.ts` owns the state (open, the page, the pending language,
// the two-press gate) and the presses; this module turns one view of that
// state into the parts of the menu, so a test reads the menu with no
// overlay around it.
//
// One structure, two shells. The desktop popup draws a head, a rail of the
// eight sections, a pane of one section, and the session block as a foot
// under both (R1757); the desktop root is MAP. The telephone draws one page at a
// time over the thumb dock (R1754, card 02 of mockup round 082): the root
// (the DEV row of an admin seat, the QUICK row, the section tiles, the
// session block), a section page, the ACCOUNT page or the music player.
// The head of a telephone page names its path and holds no control: the
// caps of the tab bar under the menu close it and go back.

import type { GameSettings } from '../settings';
import { OPTIONS_MENU } from '../../gen/presentation';
import { ABSENCE_COVER_AFTER_HOURS } from '../../gen/balance';
import { options as S, overlay as OS } from '../../gen/strings';
import { esc } from '../esc';
import { textGlyph } from '../glyph';
import { HUB_THEMES_HREF, hubSettingsHref } from '../hublink';
import { NETTEST_HREF } from '../reportsheet';
import { dockIcon, lightDot } from '../overlay/thumbdock';
import { button, linkButton } from '../choice/choice';
import {
  type Commander, type CommanderMarks, type Crumb, type Press, actionRow, backButton, callout,
  commanderCard, crumbHead, groupHead, head, legend, linkList, linkRow, logoutBlock, longLine, note, pageTitle,
  quickRow, quickTile, railItem, rootLabel, row, scopeMark, sectionTiles, sessionBlock, sessionButtons, switchCtl, tag,
  type Scope,
} from './kit';
import { ACT, audioRows, ifaceRows, mapRows, seaviewRows, socialRows, switchRow, type IfaceRowId } from './rows';
import type { MarkMode } from '../../arcade/markscale';
import { ACCOUNT_PAGE, SECTIONS, pageOf, summaryOf, type PageId, type SectionId, type SummaryFacts } from './sections';
import type { StandingView } from './gamehost';
import { devPageHtml, type DevPanelView } from './devpanel';

/** A page of the in-game menu: the telephone root, a section, ACCOUNT,
 *  the music player under AUDIO, or the DEV page of an admin seat, which
 *  is a page of the telephone alone (R1766). */
export type MenuPage = 'root' | PageId | 'player' | 'dev';

/** The acts of the in-game menu that are not shared rows (`rows.ts` ACT). */
export const MENU_ACT = {
  /** Opens a page: `data-id` names it. */
  page: 'opt-page',
  /** ✕ and the dimmed map of the desktop popup. */
  close: 'optsclose',
  /** The DEV row of the telephone root opens the DEV page (R1754, R1766). */
  dev: 'opt-dev',
  /** The standing-orders switch of the account. */
  standing: 'opt-standing',
  /** A hub row of the ACCOUNT page: `data-id` is a key of `HUB_LINKS`. */
  hub: 'opt-hub',
} as const;

/** Where each hub row of the ACCOUNT page lands (R1731): the section of
 *  the hub SETTINGS panel that holds it, or THEMES of MODDING. */
export const HUB_LINKS: Readonly<Record<string, string>> = {
  signin: hubSettingsHref('account'),
  password: hubSettingsHref('account'),
  delete: hubSettingsHref('account'),
  register: hubSettingsHref('account'),
  themes: HUB_THEMES_HREF,
  hours: hubSettingsHref('play'),
};

/** How tight the telephone root packs its parts (card 20, slot 4). */
export type RootMode = 'full' | 'mid' | 'tight';

/** The root mode of a telephone menu of this height in chrome px: the
 *  screen less the tab bar under the menu (`[presentation.options]`,
 *  R1754). */
export function rootMode(menuH: number): RootMode {
  if (menuH >= OPTIONS_MENU.phoneFullH) return 'full';
  return menuH >= OPTIONS_MENU.phoneMidH ? 'mid' : 'tight';
}

/** The DEV row of an admin seat at the head of the telephone root (card 01
 *  of mockup round 082). The statistics line is no part of the markup: the
 *  overlay writes it into the `data-devstats` line after each rebuild. */
export interface DevView {
  /** How many commits the dev lane is behind its branch (R89). */
  behind: number;
  /** The build the lane runs, or '' while the server sends no build. */
  build: string;
  /** The tooltip of the build: the hash, the branch and HEAD. */
  title: string;
  /** The rows of the DEV page, read on that page alone (R1766). */
  panel?: DevPanelView;
}

/** What the menu shows, as the overlay reads it at one render. */
export interface MenuView {
  /** The telephone shell (`isSheet()`). */
  phone: boolean;
  /** The landscape rail stands (`landscapeRail()`): the telephone takes
   *  the form of a rail and a pane beside it (round 090 card 11 B). */
  side?: boolean;
  page: MenuPage;
  /** The height of the menu in chrome px, for the telephone root mode. */
  height: number;
  s: GameSettings;
  /** The world line of the head: the world and the day of the season
   *  (`head_line`), or '' on a page with no world. The commander row
   *  names no world (R1731). */
  world: string;
  who: Commander;
  /** A meta server and a session stand behind the page. */
  meta: boolean;
  /** The account signs in with an email and a password. */
  password: boolean;
  /** The host publishes sound. */
  sound: boolean;
  /** The empire's advisor switch (R1566). */
  advisor: boolean;
  /** The standing orders, or null on a page with no account. */
  standing: StandingView | null;
  /** The length of the block list, once read. */
  blocked?: number;
  /** The language pick that waits for RELOAD NOW. */
  pendingLang: string | null;
  /** LOG OUT: its label (the gate's) and whether it is armed. */
  logout: { label: string; armed: boolean };
  /** The page wires photo mode and the REPORT sheet. */
  photo: boolean;
  report: boolean;
  /** The hub ALERTS and BLOCKED cards, or null while they wait. */
  alerts: string | null;
  chat: string | null;
  /** The music player: its title and its body, or null with no music. */
  player: { title: string; body: string } | null;
  /** What the commander row of the session block shows beside the name:
   *  the faction badge, the squad, the rank and the standing orders
   *  (R1757), or null where the page draws none. */
  marks: CommanderMarks | null;
  /** The faction badge of the commander card at the head of the ACCOUNT
   *  page, drawn at `account_badge_px` (R1766), or '' where the page knows
   *  no faction: the round mark stands in its place. */
  accountBadge?: string;
  /** The DEV row of an admin seat, or null on every other seat. */
  dev: DevView | null;
  /** The sections the menu holds, in this order (R1936). Absent is all
   *  eight. The season menu names no list, so it holds all eight (R3111).
   *  The arcade match menu names AUDIO, INTERFACE and HELP alone
   *  (arcade.md §7.2). */
  sections?: readonly SectionId[];
  /** The tiles of the telephone QUICK row. Absent is all four. */
  quick?: readonly QuickId[];
  /** The rows of INTERFACE (`rows.ts` `ifaceRows`). Absent is all seven. */
  iface?: readonly IfaceRowId[];
  /** The mark scale rows of INTERFACE (`rows.ts` `ifaceRows`, R1992).
   *  Absent is the core row: the season menu. The arcade match menu names
   *  the arcade row. */
  markRows?: readonly MarkMode[];
  /** The markup of the HELP rows, in place of the season rows: a page
   *  with other help to give writes its own (R1936). */
  help?: string;
  /** The markup of the session block, in place of the commander row and
   *  LEAVE TO THE HUB: the arcade writes LEAVE MATCH (R1936). */
  session?: string;
  /** A summary line in place of the season's, for a section whose rows
   *  the page writes itself (`help`). */
  sums?: Partial<Record<SectionId, string>>;
  /** The line of the effects row of AUDIO, for a host whose effects are
   *  not the season's: the arcade match menu (R2060). Absent is the
   *  season's line. */
  sfxWhat?: string;
  /** The scope marks the legend explains: the marks the rows of this menu
   *  carry. Absent is both marks (R2060). */
  legendScopes?: readonly Scope[];
}

/** The four tiles of the telephone QUICK row. */
export type QuickId = 'music' | 'effects' | 'shake' | 'advisor';

/** The parts of the menu. The overlay keeps each in its own element, so a
 *  change of the rail never rebuilds a range under a finger in the pane. */
export interface MenuParts {
  shell: 'desk' | 'phone' | 'side';
  head: string;
  /** The rail of the desktop popup; '' on a telephone. */
  rail: string;
  body: string;
  /** The pane foot of the desktop; '' on a telephone, where the tab bar
   *  stands under the menu (R1754). */
  foot: string;
  /** The session foot of the desktop, under the rail and the pane
   *  (R1757); '' on a telephone, where the session block closes the root. */
  session: string;
}

const CLOSE: Press = { act: MENU_ACT.close };
const page = (id: MenuPage): Press => ({ act: MENU_ACT.page, id });

/** The facts the summary lines read. */
function facts(v: MenuView): SummaryFacts {
  return {
    sound: v.sound, account: v.meta, advisor: v.advisor,
    standing: v.standing?.on, blocked: v.blocked,
  };
}

/** The four large switches of the telephone root (R1731). */
function quick(v: MenuView, narrow: boolean): string {
  const ids = v.quick ?? ['music', 'effects', 'shake', 'advisor'];
  // A row of two tiles keeps the two columns: the narrow row of four
  // would leave half of it empty.
  const four = narrow && ids.length > 2;
  const tile: Record<QuickId, () => string> = {
    music: () => quickTile({ glyph: textGlyph('♪'), name: S.quick_music, on: v.s.musicOn, press: { act: ACT.sw, key: 'musicOn' }, vol: 'musicVol', narrow: four }),
    effects: () => quickTile({ glyph: textGlyph('✶'), name: S.quick_effects, on: v.s.sfxOn, press: { act: ACT.sw, key: 'sfxOn' }, vol: 'sfxVol', narrow: four }),
    shake: () => quickTile({ glyph: textGlyph('≈'), name: S.quick_shake, on: v.s.screenshake, press: { act: ACT.sw, key: 'screenshake' }, narrow: four }),
    advisor: () => quickTile({ glyph: textGlyph('◈'), name: S.quick_advisor, on: v.advisor, press: { act: 'advisor' }, narrow: four }),
  };
  return quickRow(ids.map((id) => tile[id]()), four);
}

/** The summary line of one section: the page's own, else the season's. */
function sumOf(v: MenuView, id: SectionId, f: SummaryFacts): string {
  return v.sums?.[id] ?? summaryOf(id, v.s, f);
}

/** The sections of this menu, in their order (R1936). */
function sectionsOf(v: MenuView): typeof SECTIONS {
  return v.sections ? SECTIONS.filter((sec) => v.sections!.includes(sec.id as SectionId)) : SECTIONS;
}

/** The root of a menu with a rail: the first section the menu holds. */
function firstSection(v: MenuView): SectionId {
  return (sectionsOf(v)[0]?.id ?? 'map') as SectionId;
}

/**
 * The session block (R1757): the commander row of card 01 of round 082,
 * then LEAVE TO THE HUB and ARCADE. The cells of the commander row keep
 * the presses of the dock head of a desktop (R1749): the squad opens the
 * roster card and the rank opens STANDINGS. `compact` is the block of a
 * short telephone root.
 */
function session(v: MenuView, compact: boolean, shell: 'desk' | 'phone' = 'phone'): string {
  if (v.session !== undefined) return v.session;
  return sessionBlock({
    who: v.who, marks: v.marks, compact, solo: shell === 'desk', on: v.page === 'account',
    me: page('account'), squad: { act: 'squad' }, rank: { act: 'meta', tab: 'STANDINGS' },
    menu: { act: 'menu' }, arcade: { act: 'arcade' },
  });
}

/**
 * The DEV row at the head of the telephone root of an admin seat (R1754,
 * card 01 of mockup round 082). Its press opens the DEV page of the menu
 * (R1766), which has no other way in on a telephone since the dock head
 * is the desktop's (R1749). While the dev lane is behind its branch, the row reads the new
 * build with a light dot on its icon, and RESTART stands beside it as the
 * one solid light button (R89). The empty line under the build holds the
 * statistics line, which the overlay writes after each rebuild, so the
 * sweep of every second never rebuilds the root under a finger.
 */
function devRow(d: DevView): string {
  const hot = d.behind > 0;
  let line = '';
  if (hot) line = `${OS.head_commits({ n: d.behind })} · ${OS.head_build({ build: d.build })}`;
  else if (d.build !== '') line = S.dev_current({ build: d.build });
  const tip = d.title ? ` title="${esc(d.title)}"` : '';
  return `<div class="o-dev${hot ? ' hot' : ''}">`
    + `<button type="button" class="o-devb" data-act="${MENU_ACT.dev}" title="${esc(OS.bar_dev_title)}">`
    + `<span class="o-devi">${dockIcon('dev')}${lightDot(hot)}</span>`
    + `<span class="o-devt"><b>${esc(OS.head_dev)}</b>${line ? `<small${tip}>${esc(line)}</small>` : ''}`
    + '<small class="o-devs" data-devstats></small></span>'
    + (hot ? '' : '<span class="o-chev">›</span>') + '</button>'
    + (hot ? button('', { act: 'adm', data: { op: 'saverestart' } },
      { cls: 'o-rst', title: d.title || undefined, html: `${dockIcon('restart', 14)}<span>${esc(OS.dev_restart)}</span>` }) : '')
    + '</div>';
}

/** The PLAY rows: the advisor of the empire, and the standing orders of
 *  the account where a meta server stands behind the page. */
function playRows(v: MenuView): string {
  let out = row({
    name: S.row_advisor, what: S.row_advisor_what, scope: 'empire', changed: !v.advisor,
    control: switchCtl(v.advisor, { act: 'advisor' }, { label: S.row_advisor }),
  });
  const st = v.standing;
  if (st) {
    const control = st.on === undefined
      ? tag(S.standing_checking)
      : switchCtl(st.on, { act: MENU_ACT.standing }, { label: S.row_standing, disabled: st.busy });
    out += row({
      name: S.row_standing, what: S.row_standing_what, mark: tag(S.standing_mark),
      changed: st.on === false, control, more: longLine(S.standing_long({ hours: ABSENCE_COVER_AFTER_HOURS })),
    }) + (st.err ? note(st.err, true) : '');
  }
  return out;
}

/** The HELP rows: the actions that are not settings, then the dev stats
 *  switch (R1746), which every seat holds and which is off by default. */
function helpRows(v: MenuView): string {
  if (v.help !== undefined) return v.help;
  return actionRow(S.row_keys, S.row_keys_what, S.row_keys_btn, { act: 'keysopen' })
    + (v.photo ? actionRow(S.row_photo, S.row_photo_what, S.row_photo_btn, { act: 'photomode' }) : '')
    + (v.report ? actionRow(S.row_report, S.row_report_what, S.row_report_btn, { act: 'reportproblem' }) : '')
    // The network check is a page of its own, linked the way the REPORT
    // sheet links it (R1707).
    + row({
      name: S.row_nettest, what: S.row_nettest_what,
      control: linkButton(S.row_nettest_btn, NETTEST_HREF, { variant: 'primary' }),
    })
    + switchRow(v.s, 'devStats', S.row_devstats, S.row_devstats_what);
}

/** The DEVICE mark of ALERTS, where the section holds the hub card. */
function sectionMark(v: MenuView, id: SectionId): string {
  return id === 'alerts' && v.meta ? ` ${scopeMark('device')}` : '';
}

/** The rows of one section, under its title. A telephone page draws no
 *  title (`titled` false): its head names the section (R1754). */
function sectionBody(v: MenuView, id: SectionId, titled: boolean): string {
  const sec = pageOf(id);
  const title = titled
    ? pageTitle({ glyph: sec.glyph, name: sec.name, what: sec.what, mark: sectionMark(v, id) }) : '';
  const wait = note(S.standing_checking);
  switch (id) {
    case 'map':
      return title + mapRows(v.s);
    case 'audio': {
      const player = v.player
        ? actionRow(S.row_player, S.row_player_what, S.row_player_btn, page('player')) : undefined;
      return title + audioRows(v.s, { sound: v.sound, player, sfxWhat: v.sfxWhat })
        + (v.sound ? note(S.vol_note) : '');
    }
    case 'iface':
      return title + ifaceRows(v.s, {
        pendingLang: v.pendingLang, rows: v.iface, marks: v.markRows ?? ['core'], desk: !v.phone,
      });
    case 'play':
      return title + playRows(v);
    case 'alerts':
      return title + (!v.meta ? note(S.alerts_none) : v.alerts ?? wait);
    case 'social':
      // The two count switches, then the block list of the hub (R1759).
      return title + socialRows(v.s, { desk: !v.phone })
        + (!v.meta ? note(S.chat_none) : (v.chat ?? wait) + note(S.chat_how));
    case 'seaview':
      return title + seaviewRows(v.s);
    case 'help':
      return title + helpRows(v);
  }
}

/** The ACCOUNT page: the commander, the guest seat, the hub rows, and
 *  LOG OUT at the foot (on the desktop, in the pinned pane foot). */
function accountBody(v: MenuView): string {
  const guest = v.meta && v.who.guest === true;
  const hub = (id: keyof typeof HUB_LINKS, name: string, what: string, bad = false): string =>
    linkRow(name, what, { act: MENU_ACT.hub, id }, bad);
  const signin = hub('signin', S.link_signin, S.link_signin_what);
  const hours = hub('hours', S.link_hours, S.link_hours_what);
  const del = hub('delete', S.link_delete, S.link_delete_what, true);
  const links = guest
    ? [signin, hours, del]
    : [signin, ...(v.password ? [hub('password', S.link_password, S.link_password_what)] : []),
      del, hub('themes', S.link_themes, S.link_themes_what), hours];
  return commanderCard(v.who, { big: true, badge: v.accountBadge })
    + (guest ? callout(S.guest_lede, button(S.register, { act: MENU_ACT.hub, id: 'register' }, { variant: 'primary', title: S.register_title })) : '')
    + (v.meta
      ? groupHead(S.acc_hub, S.acc_hub_aside) + linkList(links) + note(S.hub_foot)
      : note(S.account_none))
    + (v.phone ? logout(v) : '');
}

/** LOG OUT: two presses through the overlay's gate (R696). The armed face
 *  LOG OUT? shares one cell with LOG OUT (R2069). */
function logout(v: MenuView): string {
  return logoutBlock({
    label: v.logout.label, armLabel: S.logout_armed, armed: v.logout.armed,
    what: v.logout.armed ? S.logout_armed_what : S.logout_what,
    press: { act: 'logout', arm: true },
  });
}

/** The music player as a page under AUDIO. */
function playerBody(v: MenuView, titled: boolean): string {
  if (!v.player) return note(S.no_audio);
  return (titled ? pageTitle({ glyph: textGlyph('♪'), name: v.player.title, what: S.row_player_what }) : '')
    + `<div class="mplayer">${v.player.body}</div>`;
}

/** The body of a page that is not the root. */
function pageBody(v: MenuView, p: Exclude<MenuPage, 'root' | 'dev'>, titled: boolean): string {
  if (p === 'account') return accountBody(v);
  if (p === 'player') return playerBody(v, titled);
  return sectionBody(v, p, titled);
}

/**
 * The desktop popup: a head, the rail and the pane of one page, and the
 * session foot under both (R1757). The foot spans the popup, because the
 * three cells of the commander row and their words need more room than
 * the 210 px rail holds.
 */
function desk(v: MenuView): MenuParts {
  // The DEV page is the telephone's: the desktop holds the `#admin` card.
  // The root of the desktop is the first section the menu holds.
  const secs = sectionsOf(v);
  const active: Exclude<MenuPage, 'root' | 'dev'> = v.page === 'root' || v.page === 'dev' ? firstSection(v) : v.page;
  const lit = active === 'player' ? 'audio' : active;
  const f = facts(v);
  const rail = secs.map((sec) => railItem({
    glyph: sec.glyph, name: sec.name, sum: sumOf(v, sec.id as SectionId, f), press: page(sec.id),
  }, sec.id === lit)).join('');
  let foot = '';
  if (active === 'account') foot = logout(v);
  else if (active !== 'help' && active !== 'player') foot = legend(v.legendScopes);
  return {
    shell: 'desk',
    head: head({ title: S.title, glyph: textGlyph('☸'), world: v.world, esc: true, close: CLOSE }),
    rail,
    body: active === 'player'
      ? backButton(S.sec_audio, page('audio')) + playerBody(v, true)
      : pageBody(v, active, true),
    foot,
    session: session(v, false, 'desk'),
  };
}

/**
 * The menu beside the landscape rail (round 090 card 11 B, R1848): the
 * form of a rail and a pane that the hub SETTINGS panel takes on a wide
 * screen (R1733). The sections stand as a ruled list of 44 px rows with
 * no summary, ACCOUNT under them (and DEV over them on an admin seat),
 * the open page beside them in its desktop form, and MENU and ARCADE in
 * the head. The MENU cap of the rail closes the menu, so the head holds
 * no ✕, and the QUICK row of the telephone root stands in no place: its
 * four switches stay in their sections.
 */
function side(v: MenuView): MenuParts {
  const d = desk({ ...v, phone: false });
  const lit = v.page === 'root' ? firstSection(v) : v.page === 'player' ? 'audio' : v.page;
  const item = (glyph: string, name: string, press: Press, on: boolean) => railItem({ glyph, name, sum: '', press }, on);
  const rail = (v.dev ? item(dockIcon('dev'), OS.head_dev, { act: MENU_ACT.dev }, lit === 'dev') : '')
    + sectionsOf(v).map((sec) => item(sec.glyph, sec.name, page(sec.id), sec.id === lit)).join('')
    + item(ACCOUNT_PAGE.glyph, ACCOUNT_PAGE.name, page('account'), lit === 'account');
  const dev = v.page === 'dev';
  return {
    shell: 'side',
    head: head({ title: S.title, glyph: textGlyph('☸'), world: v.world, tail: sessionButtons({ act: 'menu' }, { act: 'arcade' }) }),
    rail,
    body: dev ? devBody(v) : d.body,
    foot: dev ? '' : d.foot,
    session: '',
  };
}

/** The root of the path in the head of a telephone page. */
const ROOT: Crumb = { glyph: textGlyph('☸'), name: S.title };

/** The path of a telephone page, from OPTIONS to the page (R1754). */
export function pathOf(p: MenuPage, playerTitle = ''): Crumb[] {
  if (p === 'root') return [ROOT];
  if (p === 'account') return [ROOT, { glyph: '', name: S.sec_account }];
  if (p === 'dev') return [ROOT, { glyph: '', name: OS.head_dev }];
  if (p === 'player') {
    return [ROOT, { glyph: '', name: pageOf('audio').name },
      { glyph: textGlyph('♪'), name: playerTitle || S.row_player }];
  }
  const sec = pageOf(p);
  return [ROOT, { glyph: sec.glyph, name: sec.name }];
}

/** The page the ‹ BACK cap of the tab bar opens from a telephone page:
 *  AUDIO from the music player, the root from every other page, and none
 *  from the root (R1754). */
export function parentOf(p: MenuPage): MenuPage | null {
  if (p === 'root') return null;
  return p === 'player' ? 'audio' : 'root';
}

/**
 * The telephone (R1754, card 02 of mockup round 082): the menu covers the
 * screen from the top edge down to the tab bar, which stays under it. The
 * head holds the title and the path alone: ✕ CLOSE and ‹ BACK are the caps
 * of the tab bar, so the menu draws no close, no back and no BACK TO THE
 * MAP bar, and a page draws no large title. The root names the world and
 * the day beside the title, except in the tight mode, where the head has
 * no room for the line.
 */
function phone(v: MenuView): MenuParts {
  const mode = rootMode(v.height);
  if (v.page === 'root') {
    const f = facts(v);
    const tiles = sectionsOf(v).map((sec) => ({
      glyph: sec.glyph, name: sec.name, press: page(sec.id),
      sum: mode === 'tight' ? '' : sumOf(v, sec.id as SectionId, f),
    }));
    return phoneParts(crumbHead(pathOf('root'), { world: mode === 'tight' ? undefined : v.world }),
      `<div class="o-root ${mode}">`
        + (v.dev ? devRow(v.dev) : '')
        + rootLabel(S.quick) + quick(v, mode !== 'full')
        + rootLabel(S.sections) + sectionTiles(tiles)
        + rootLabel(S.session) + session(v, mode !== 'full') + '</div>');
  }
  if (v.page === 'dev') return phoneParts(crumbHead(pathOf('dev')), devBody(v));
  const mark = v.page === 'account' || v.page === 'player' ? '' : sectionMark(v, v.page);
  return phoneParts(crumbHead(pathOf(v.page, v.player?.title), { mark }), pageBody(v, v.page, false));
}

/** The DEV page of an admin seat (R1766): the rows of the `#admin` card,
 *  as kit rows. */
function devBody(v: MenuView): string {
  return v.dev?.panel ? devPageHtml(v.dev.panel) : '';
}

/** The parts of a telephone page: the head and the body alone. */
function phoneParts(head: string, body: string): MenuParts {
  return { shell: 'phone', head, rail: '', body, foot: '', session: '' };
}

/** The parts of the menu for one view. */
export function menuParts(v: MenuView): MenuParts {
  if (!v.phone) return desk(v);
  return v.side ? side(v) : phone(v);
}
